import { useQuery } from "@tanstack/react-query";
import { workflowOneApi } from "./axios";
import {
  DataI,
  ServiceResponseI,
  PropsWorkflowOneI,
  WorkflowOneI,
  ObjPostI,
  WorkflowOneForNextProcessI,
} from "../../interfaces";
import { ValidateCurrentToken, LogoutSystem } from "../../helpers";
import { AxiosError } from "axios";

const getWorkflowOne = async ({
  pagination,
  sorting,
  globalFilter,
  client,
}: PropsWorkflowOneI): Promise<ServiceResponseI<DataI<WorkflowOneI>>> => {
  try {
    const validateToken = ValidateCurrentToken();

    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const params = new URLSearchParams();

    params.append("page", (pagination!.pageIndex + 1).toString());
    params.append("limit", pagination!.pageSize.toString());
    params.append("search", globalFilter ?? "");
    params.append("sorting", JSON.stringify(sorting ?? []));
    client && params.append("client", client.toString());

    //headers: { 'Authorization': validateToken
    const { data } = await workflowOneApi.get<
      ServiceResponseI<DataI<WorkflowOneI>>
    >("", { params, headers: { Authorization: validateToken } });
    return data;
  } catch (err) {
    console.log(err);
    const obj = (err as AxiosError).response;

    if (obj?.status === 401) {
      console.error(obj.statusText);
      LogoutSystem();
    }

    throw new Error((obj?.data as Error).message);
  }
};

export const getAllWorkflowOne = async (): Promise<
  ServiceResponseI<WorkflowOneI[]>
> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const { data } = await workflowOneApi.get<ServiceResponseI<WorkflowOneI[]>>(
      `/all-no-pagination`,
      { headers: { Authorization: validateToken } }
    );

    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) {
      console.error(obj.statusText);
      LogoutSystem();
    }
    throw new Error((obj?.data as Error).message);
  }
};

export const getWorkflowOneByContainerForNextProcess = async (
  container: string
): Promise<ServiceResponseI<WorkflowOneForNextProcessI>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const { data } = await workflowOneApi.get<
      ServiceResponseI<WorkflowOneForNextProcessI>
    >(`/by-container-for-next-process/${container}`, {
      headers: { Authorization: validateToken },
    });

    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) {
      console.error(obj.statusText);
      LogoutSystem();
    }
    throw new Error((obj?.data as Error).message);
  }
};

export const generateWfOnePdfReportbyId = async (
  id: number
): Promise<ServiceResponseI<any>> => {
  try {
    // const validateToken = ValidateCurrentToken();
    // if (!validateToken) throw new Error("TOKEN INVALIDO");

    const { data } = await workflowOneApi.get<ServiceResponseI<any>>(
      `/generate-pdf-report/${id}`
      // { headers: { Authorization: validateToken } }
    );

    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) {
      console.error(obj.statusText);
      LogoutSystem();
    }
    throw new Error((obj?.data as Error).message);
  }
};

export const workflowOneCount = async (
  type = "hoy"
): Promise<ServiceResponseI<{ total: number }>> => {
  try {
    const params = new URLSearchParams();

    type && params.append("type", type.toString());

    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");
    const { data } = await workflowOneApi.get<
      ServiceResponseI<{ total: number }>
    >(`/count`, { params, headers: { Authorization: validateToken } });
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) {
      console.error(obj.statusText);
      LogoutSystem();
    }
    throw new Error((obj?.data as Error).message);
  }
};

export const workflowOneSendReportById = async <T>(
  getForms: T
): Promise<ServiceResponseI<string>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");
    const { data } = await workflowOneApi.post<ServiceResponseI<string>>(
      `/send-report-by-workflow-id`,
      getForms,
      {
        headers: { Authorization: validateToken },
      }
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) {
      LogoutSystem();
    }
    throw new Error((obj?.data as Error).message);
  }
};

// export const workflowOnePost = async <T>(
//   getForms: T
// ): Promise<ServiceResponseI<ObjPostI>> => {
//   try {
//     const validateToken = ValidateCurrentToken();
//     if (!validateToken) throw new Error("TOKEN INVALIDO");
//     const { data } = await workflowOneApi.post<ServiceResponseI<ObjPostI>>(
//       "",
//       getForms,
//       { headers: { Authorization: validateToken } }
//     );
//     return data;
//   } catch (err: any) {
//     if (err.code === "ERR_NETWORK") {
//       return {
//         data: {
//           id: 0,
//           alert: "Se guardará y notificará, cuando haya conexión a Internet",
//           variant: "warning",
//         },
//         res: {} as any,
//         req: {} as any,
//       };
//     }
//     const obj = (err as AxiosError).response;
//     if (obj?.status === 401) {
//       console.error(obj.statusText);
//       LogoutSystem();
//     }
//     throw new Error((obj?.data as Error).message);
//   }
// };

export const workflowOnePost = async <T>(
  getForms: T
): Promise<ServiceResponseI<ObjPostI>> => {
  try {
    // Validar el token como antes
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    // URL del endpoint - usando exactamente la misma URL que axios
    const url = `${process.env.REACT_APP_BACKEND!}api/workflow-one`;

    // Generar un ID único para esta transacción
    const offlineId = `offline_${Date.now()}_${Math.random()
      .toString(36)
      .substr(2, 9)}`;

    // Crear una promesa para manejar la comunicación con el Service Worker
    return new Promise((resolve, reject) => {
      // Verificar si el Service Worker está disponible
      if (!navigator.serviceWorker || !navigator.serviceWorker.controller) {
        console.warn(
          "Service Worker no está disponible, intentando petición normal"
        );
        // Hacer la petición normal como fallback
        workflowOneApi
          .post<ServiceResponseI<ObjPostI>>("", getForms, {
            headers: { Authorization: validateToken },
          })
          .then(({ data }) => resolve(data))
          .catch((err) => {
            if (err.code === "ERR_NETWORK") {
              resolve({
                data: {
                  id: 0,
                  alert: "Error de red. Intenta de nuevo cuando haya conexión",
                  variant: "error",
                },
                res: {} as any,
                req: {} as any,
              });
            } else {
              reject(err);
            }
          });
        return;
      }

      // Crear un canal de mensajes para recibir la respuesta del Service Worker
      const messageChannel = new MessageChannel();

      // Escuchar la respuesta del Service Worker
      messageChannel.port1.onmessage = (event) => {
        if (event.data.error) {
          reject(new Error(event.data.error));
        } else {
          // Devolver una respuesta simulada para la UI con mejor información
          resolve({
            data: {
              id: parseInt(offlineId.split("_")[1]) || 0,
              alert: navigator.onLine
                ? "Datos guardados con éxito"
                : "Datos guardados. Se sincronizarán cuando haya conexión.",
              variant: navigator.onLine ? "info" : "warning",
            },
            res: {} as any,
            req: {} as any,
            // isOffline: true,
          });
        }
      };

      // Enviar el mensaje al Service Worker
      navigator.serviceWorker.controller.postMessage(
        {
          type: "ENQUEUE_POST",
          url,
          data: getForms,
          offlineId,
          headers: {
            "Content-Type": "application/json",
            Authorization: validateToken,
            "X-Offline-ID": offlineId,
          },
        },
        [messageChannel.port2]
      );
    });
  } catch (err: any) {
    // Manejar errores específicos
    if (err.message === "TOKEN INVALIDO") {
      console.error("Token inválido");
      LogoutSystem();
      throw err;
    }

    const obj = (err as AxiosError).response;
    if (obj?.status === 401) {
      console.error(obj.statusText);
      LogoutSystem();
    }

    // Si hay otro error
    return {
      data: {
        id: 0,
        alert: "Error al guardar los datos. Por favor, intenta de nuevo.",
        variant: "error",
      },
      res: {} as any,
      req: {} as any,
      // isOffline: true,
    };
  }
};

export const workflowOnePut = async <T>(
  id: number,
  updateForms: T
): Promise<ServiceResponseI<string>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");
    const { data } = await workflowOneApi.put<ServiceResponseI<string>>(
      `/${id}`,
      updateForms,
      { headers: { Authorization: validateToken } }
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) {
      console.error(obj.statusText);
      LogoutSystem();
    }
    throw new Error((obj?.data as Error).message);
  }
};

export const workflowOneById = async (
  id: number
): Promise<ServiceResponseI<WorkflowOneI>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");
    const { data } = await workflowOneApi.get<ServiceResponseI<WorkflowOneI>>(
      `/${id}`,
      { headers: { Authorization: validateToken } }
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) {
      console.error(obj.statusText);
      LogoutSystem();
    }
    throw new Error((obj?.data as Error).message);
  }
};

export const workflowOneForExternalById = async (
  id: string
): Promise<ServiceResponseI<WorkflowOneI>> => {
  try {
    // const validateToken = ValidateCurrentToken();
    // if (!validateToken) throw new Error("TOKEN INVALIDO");
    const encodedId = encodeURIComponent(id);

    const { data } = await workflowOneApi.get<ServiceResponseI<WorkflowOneI>>(
      `/external/${encodedId}`
      // { headers: { Authorization: validateToken } }
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) {
      console.error(obj.statusText);
      LogoutSystem();
    }
    throw new Error((obj?.data as Error).message);
  }
};

export const workflowOneDelete = async (
  id: number
): Promise<ServiceResponseI<string>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");
    const { data } = await workflowOneApi.delete<ServiceResponseI<string>>(
      `/${id}`,
      { headers: { Authorization: validateToken } }
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) {
      console.error(obj.statusText);
      LogoutSystem();
    }
    throw new Error((obj?.data as Error).message);
  }
};

export const useWorkflowOne = ({
  sorting,
  pagination,
  globalFilter,
  client,
}: PropsWorkflowOneI) => {
  const workflowOnesQuery = useQuery(
    ["workflowOne", { pagination, sorting, globalFilter, client }],
    () => getWorkflowOne({ pagination, sorting, globalFilter, client }),
    {
      refetchOnWindowFocus: false,
      keepPreviousData: true,
    }
  );

  return {
    workflowOnesQuery,
  };
};
